<template>
  <div class="time-switch-day-ring" :style="ringStyle" aria-hidden="true">
    <svg class="time-switch-day-ring__svg" :width="size" :height="size" viewBox="0 0 100 100">
      <!-- Gray background track -->
      <circle class="time-switch-day-ring__track" cx="50" cy="50" :r="radius" :stroke-width="normalizedStrokeWidth" />
      <!-- ON-time arc segments drawn as SVG paths (north-origin, clockwise) -->
      <path
        v-for="(d, i) in arcPaths"
        :key="i"
        class="time-switch-day-ring__segment"
        :d="d"
        :stroke-width="normalizedStrokeWidth" />
      <!-- Current-time indicator dot -->
      <template v-if="showNow">
        <circle
          class="time-switch-day-ring__now-shadow"
          :cx="nowPoint.x"
          :cy="nowPoint.y"
          :r="normalizedStrokeWidth * 0.68" />
        <circle class="time-switch-day-ring__now" :cx="nowPoint.x" :cy="nowPoint.y" :r="normalizedStrokeWidth * 0.46" />
      </template>
    </svg>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { getTimeSwitchEntries, normalizeAnchor } from '../../utils/switchAutomation';

interface Props {
  config: Record<string, unknown>;
  size?: number;
  strokeWidth?: number;
  color?: string;
  trackColor?: string;
  showNow?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  size: 42,
  strokeWidth: 4,
  color: 'rgb(14, 165, 233)',
  trackColor: 'rgba(148, 163, 184, 0.22)',
  showNow: true,
});

const MINUTES_PER_DAY = 24 * 60;

const radius = computed(() => 50 - props.strokeWidth * 2.5);
const normalizedStrokeWidth = computed(() => props.strokeWidth * 2.4);

const ringStyle = computed(() => ({
  width: `${props.size}px`,
  height: `${props.size}px`,
  '--time-switch-ring-color': props.color,
  '--time-switch-ring-track': props.trackColor,
}));

/**
 * Convert a time ratio (0 = midnight, 1 = midnight+24h) to an (x, y) SVG point.
 * Convention: 0:00 at top (north), progressing clockwise.
 *   ratio=0.00 → top    (0:00)
 *   ratio=0.25 → right  (6:00)
 *   ratio=0.50 → bottom (12:00)
 *   ratio=0.75 → left   (18:00)
 */
function ratioToPoint(ratio: number): { x: number; y: number } {
  // Start at south (+π/2), go clockwise (increasing angle in SVG y-down space)
  const angle = ratio * 2 * Math.PI + Math.PI / 2;
  return {
    x: 50 + radius.value * Math.cos(angle),
    y: 50 + radius.value * Math.sin(angle),
  };
}

/**
 * Build an SVG arc path from startRatio to endRatio (clockwise).
 * Uses the A (arc) command: sweep-flag=1 means clockwise in SVG screen coords.
 */
function makeArcPath(startRatio: number, endRatio: number): string {
  const r = radius.value;
  const span = endRatio - startRatio;

  if (span >= 1.0) {
    // Full circle: split into two half-arcs to avoid the degenerate same-point case
    const p1 = ratioToPoint(startRatio);
    const p2 = ratioToPoint(startRatio + 0.5);
    return `M ${p1.x} ${p1.y} A ${r} ${r} 0 0 1 ${p2.x} ${p2.y} ` + `A ${r} ${r} 0 0 1 ${p1.x} ${p1.y}`;
  }

  const p1 = ratioToPoint(startRatio);
  const p2 = ratioToPoint(endRatio);
  const largeArc = span > 0.5 ? 1 : 0;
  return `M ${p1.x} ${p1.y} A ${r} ${r} 0 ${largeArc} 1 ${p2.x} ${p2.y}`;
}

const nowPoint = computed(() => {
  const now = new Date();
  const minutes = now.getHours() * 60 + now.getMinutes() + now.getSeconds() / 60;
  return ratioToPoint(minutes / MINUTES_PER_DAY);
});

const arcPaths = computed<string[]>(() => {
  const today = new Date();
  const todayDay = today.getDay();
  const previousDay = (todayDay + 6) % 7;
  const rawSegments: Array<{ start: number; end: number }> = [];

  for (const entry of getTimeSwitchEntries(props.config)) {
    if (typeof entry.day !== 'number') continue;

    const onAnchor = normalizeAnchor(entry.on_time, '08:00');
    const offAnchor = normalizeAnchor(entry.off_time, '22:00');

    // Sunrise/sunset anchors can't be placed on the ring without live sun data
    if (onAnchor.type !== 'time' || offAnchor.type !== 'time') {
      continue;
    }

    const onMinutes = timeToMinutes(onAnchor.time ?? '08:00');
    const offMinutes = timeToMinutes(offAnchor.time ?? '22:00');
    const wrapsOvernight = offMinutes <= onMinutes;

    if (entry.day === todayDay) {
      if (wrapsOvernight) {
        // Today's segment runs from ON time to end of day; the midnight→OFF part
        // is handled by the previousDay branch below (if yesterday also wraps)
        rawSegments.push({ start: onMinutes / MINUTES_PER_DAY, end: 1 });
      } else {
        rawSegments.push({
          start: onMinutes / MINUTES_PER_DAY,
          end: offMinutes / MINUTES_PER_DAY,
        });
      }
    }

    // Overnight carry-over: yesterday's schedule may still be ON past midnight
    if (entry.day === previousDay && wrapsOvernight && offMinutes > 0) {
      rawSegments.push({ start: 0, end: offMinutes / MINUTES_PER_DAY });
    }
  }

  return mergeSegments(rawSegments)
    .filter((seg) => seg.end - seg.start > 0.001)
    .map((seg) => makeArcPath(seg.start, seg.end));
});

function timeToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
}

function mergeSegments(segs: Array<{ start: number; end: number }>) {
  const sorted = segs
    .map((s) => ({ start: clamp01(s.start), end: clamp01(s.end) }))
    .filter((s) => s.end > s.start)
    .sort((a, b) => a.start - b.start);

  const merged: Array<{ start: number; end: number }> = [];
  for (const s of sorted) {
    const prev = merged[merged.length - 1];
    if (!prev || s.start > prev.end) {
      merged.push({ ...s });
    } else {
      prev.end = Math.max(prev.end, s.end);
    }
  }
  return merged;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
</script>

<style scoped>
.time-switch-day-ring {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.time-switch-day-ring__svg {
  overflow: visible;
}

.time-switch-day-ring__track,
.time-switch-day-ring__segment {
  fill: none;
}

.time-switch-day-ring__track {
  stroke: var(--time-switch-ring-track);
}

.time-switch-day-ring__segment {
  stroke: var(--time-switch-ring-color);
  stroke-linecap: round;
}

.time-switch-day-ring__now-shadow {
  fill: rgba(255, 255, 255, 0.22);
}

.time-switch-day-ring__now {
  fill: #fff;
}
</style>
